// Behavior for the thespecial.app landing page. Every hook is a data-attribute
// in app/views/homepage/*, and each block is a no-op when its elements are absent.

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initNav() {
  const nav = document.querySelector('[data-nav]');
  if (!nav) return;
  const update = () => nav.toggleAttribute('data-scrolled', window.scrollY > 24);
  update();
  window.addEventListener('scroll', update, { passive: true });
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function initSheet() {
  const sheet = document.querySelector('[data-sheet]');
  const opener = document.querySelector('[data-sheet-open]');
  if (!sheet || !opener) return;
  const setOpen = (open, { restoreFocus = true } = {}) => {
    sheet.hidden = !open;
    opener.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) sheet.querySelector(FOCUSABLE)?.focus();
    else if (restoreFocus) opener.focus();
  };
  opener.addEventListener('click', () => setOpen(true));
  sheet.querySelectorAll('[data-sheet-close]').forEach((el) => el.addEventListener('click', () => setOpen(false)));
  document.addEventListener('keydown', (e) => {
    if (sheet.hidden) return;
    if (e.key === 'Escape') return setOpen(false);
    if (e.key !== 'Tab') return;
    // Keep focus inside the open sheet.
    const items = [...sheet.querySelectorAll(FOCUSABLE)];
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
  // Opening the waitlist from inside the sheet hands over without stealing focus back.
  document.addEventListener('waitlist:open', () => !sheet.hidden && setOpen(false, { restoreFocus: false }));
}

const EMAIL_HINT = 'That doesn’t look like an email address. Check it and try again.';
const GENERIC_ERROR = 'Something went wrong. Try again in a moment.';

// Validates and posts a waitlist form to its action as JSON. Resolves to
// { status: 'ok' | 'empty' | 'invalid' | 'limited' | 'error' }; the caller owns the UI.
async function postWaitlist(form, emailInput) {
  const email = emailInput.value.trim();
  if (!email) return { status: 'empty' };
  if (!emailInput.validity.valid) return { status: 'invalid' };

  try {
    const data = new FormData(form);
    const response = await fetch(form.action, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        website: data.get('website'),
        newsletter_recipient: {
          email,
          neighborhood: data.get('newsletter_recipient[neighborhood]') || undefined
        }
      })
    });

    if (response.ok) return { status: 'ok' };
    if (response.status === 422) {
      const body = await response.json().catch(() => ({}));
      return { status: body.errors?.email ? 'invalid' : 'error' };
    }
    if (response.status === 429) return { status: 'limited' };
    return { status: 'error' };
  } catch (error) {
    return { status: 'error' };
  }
}

function initWaitlist() {
  const dialog = document.getElementById('waitlist-dialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const formView = dialog.querySelector('[data-waitlist-form-view]');
  const successView = dialog.querySelector('[data-waitlist-success]');
  const form = dialog.querySelector('[data-waitlist-form]');
  const emailInput = form.querySelector('input[type="email"]');
  const fieldError = form.querySelector('[data-field-error]');
  const formError = form.querySelector('[data-form-error]');
  const submit = form.querySelector('[data-submit]');
  const spinner = form.querySelector('[data-spinner]');
  const label = form.querySelector('[data-submit-label]');
  let loading = false;
  let opener = null;

  const clearErrors = () => {
    fieldError.hidden = true;
    fieldError.textContent = '';
    formError.hidden = true;
    formError.textContent = '';
    emailInput.removeAttribute('aria-invalid');
  };
  const showFieldError = (message) => {
    fieldError.textContent = message;
    fieldError.hidden = false;
    emailInput.setAttribute('aria-invalid', 'true');
    emailInput.focus();
  };
  const showFormError = (message) => {
    formError.textContent = message;
    formError.hidden = false;
  };
  const setLoading = (on) => {
    loading = on;
    submit.disabled = on;
    spinner.hidden = !on;
    label.textContent = on ? 'Joining…' : 'Join the Waitlist';
    form.setAttribute('aria-busy', String(on));
  };

  const open = (trigger) => {
    document.dispatchEvent(new CustomEvent('waitlist:open'));
    opener = trigger instanceof HTMLElement ? trigger : opener;
    document.body.style.overflow = '';
    // Reopening after a success starts a fresh form.
    formView.hidden = false;
    successView.hidden = true;
    clearErrors();
    if (!dialog.open) dialog.showModal();
    emailInput.focus();
  };

  document.querySelectorAll('[data-waitlist-open]').forEach((el) => el.addEventListener('click', () => open(el)));
  dialog.querySelectorAll('[data-dialog-close]').forEach((el) => el.addEventListener('click', () => dialog.close()));
  // Click on the backdrop closes the dialog.
  dialog.addEventListener('click', (e) => e.target === dialog && dialog.close());
  dialog.addEventListener('close', () => opener?.focus());

  emailInput.addEventListener('input', clearErrors);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;
    clearErrors();

    setLoading(true);
    const result = await postWaitlist(form, emailInput);
    setLoading(false);

    if (result.status === 'ok') {
      formView.hidden = true;
      successView.hidden = false;
      successView.focus();
    } else if (result.status === 'empty') {
      showFieldError('Enter your email to join.');
    } else if (result.status === 'invalid') {
      showFieldError(EMAIL_HINT);
    } else if (result.status === 'limited') {
      showFormError('Too many attempts. Please try again in a bit.');
    } else {
      showFormError(GENERIC_ERROR);
    }
  });

  // Without JS a failed signup redirects back with an error toast: reopen the form.
  if (document.querySelector('[data-waitlist-error]')) open();
}

// The hero's one-click signup: email plus a button, success shown in place.
function initInlineWaitlist() {
  const root = document.querySelector('[data-waitlist-inline]');
  if (!root) return;
  const form = root.querySelector('[data-waitlist-inline-form]');
  const success = root.querySelector('[data-waitlist-inline-success]');
  const emailInput = form.querySelector('input[type="email"]');
  const fieldError = form.querySelector('[data-field-error]');
  const submit = form.querySelector('[data-submit]');
  const spinner = form.querySelector('[data-spinner]');
  const arrow = form.querySelector('[data-submit-arrow]');
  const label = form.querySelector('[data-submit-label]');
  let loading = false;

  const clearError = () => {
    fieldError.hidden = true;
    fieldError.textContent = '';
    emailInput.removeAttribute('aria-invalid');
  };
  const showError = (message, { focus = true } = {}) => {
    fieldError.textContent = message;
    fieldError.hidden = false;
    if (focus) emailInput.setAttribute('aria-invalid', 'true');
    if (focus) emailInput.focus();
  };
  const setLoading = (on) => {
    loading = on;
    submit.disabled = on;
    spinner.hidden = !on;
    arrow.hidden = on;
    label.textContent = on ? 'Joining…' : 'Join the Waitlist';
    form.setAttribute('aria-busy', String(on));
  };

  emailInput.addEventListener('input', clearError);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;
    clearError();

    setLoading(true);
    const result = await postWaitlist(form, emailInput);
    setLoading(false);

    if (result.status === 'ok') {
      form.hidden = true;
      success.hidden = false;
      success.focus();
    } else if (result.status === 'empty') {
      showError('Enter your email to join.');
    } else if (result.status === 'invalid') {
      showError(EMAIL_HINT);
    } else if (result.status === 'limited') {
      showError('Too many attempts. Please try again in a bit.', { focus: false });
    } else {
      showError(GENERIC_ERROR, { focus: false });
    }
  });
}

function initToasts() {
  document.querySelectorAll('[data-toast]').forEach((toast) => {
    const hide = () => toast.remove();
    toast.querySelector('[data-toast-close]')?.addEventListener('click', hide);
    setTimeout(hide, 8000);
  });
}

function initReveal() {
  const items = document.querySelectorAll('[data-reveal]');
  items.forEach((el) => el.style.setProperty('--reveal-delay', `${el.dataset.revealDelay || 0}ms`));
  if (!('IntersectionObserver' in window)) return items.forEach((el) => el.classList.add('is-visible'));
  const observer = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }),
    { threshold: 0.15 }
  );
  items.forEach((el) => observer.observe(el));
}

function initMap() {
  const map = document.querySelector('[data-map]');
  if (!map) return;
  const canvas = map.querySelector('[data-map-canvas]');
  const card = map.querySelector('[data-place-card]');
  const pins = [...map.querySelectorAll('[data-pin]')];
  const empty = map.querySelector('[data-map-empty]');
  let active = null;

  const closeCard = () => {
    card.hidden = true;
    active = null;
  };

  const openCard = (pin) => {
    card.querySelector('[data-card-name]').textContent = pin.dataset.name;
    card.querySelector('[data-card-category]').textContent = pin.dataset.category;
    card.querySelector('[data-card-deal]').textContent = pin.dataset.deal;
    card.querySelector('[data-card-hours]').textContent = pin.dataset.hours;
    card.querySelector('[data-card-area]').textContent = pin.dataset.area;
    const photo = card.querySelector('[data-card-photo]');
    photo.hidden = !pin.dataset.photo;
    if (pin.dataset.photo) photo.src = pin.dataset.photo;
    card.hidden = false;
    active = pin;
    // Place the card above the pin, clamped inside the map.
    const box = map.getBoundingClientRect();
    const pinBox = pin.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const left = Math.min(Math.max(pinBox.left - box.left + pinBox.width / 2 - 40, 12), box.width - cardBox.width - 12);
    let top = pinBox.top - box.top - cardBox.height - 12;
    if (top < 76) top = pinBox.bottom - box.top + 12;
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
  };

  // Hover opens the card only on devices that can hover; touch uses tap.
  const canHover = window.matchMedia('(hover: hover)').matches;
  pins.forEach((pin) => {
    if (canHover) pin.addEventListener('mouseenter', () => openCard(pin));
    pin.addEventListener('focus', () => openCard(pin));
    pin.addEventListener('click', () => (active === pin ? closeCard() : openCard(pin)));
  });
  document.addEventListener('keydown', (e) => e.key === 'Escape' && closeCard());
  map.addEventListener('click', (e) => !e.target.closest('[data-pin], [data-place-card]') && closeCard());

  // Open the first happy-hour pin by default, like the mockup.
  const first = pins.find((p) => p.dataset.pin === 'happy') || pins[0];
  if (first && !reduceMotion()) setTimeout(() => openCard(first), 600);
  else if (first) openCard(first);

  document.querySelectorAll('[data-legend]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const on = btn.getAttribute('aria-pressed') !== 'true';
      btn.setAttribute('aria-pressed', String(on));
      pins.filter((p) => p.dataset.pin === btn.dataset.legend).forEach((p) => p.toggleAttribute('data-hidden', !on));
      if (active && active.hasAttribute('data-hidden')) closeCard();
      empty.hidden = pins.some((p) => !p.hasAttribute('data-hidden'));
    })
  );

  let zoom = 1;
  map.querySelectorAll('[data-zoom]').forEach((btn) =>
    btn.addEventListener('click', () => {
      zoom = Math.min(1.6, Math.max(1, zoom + (btn.dataset.zoom === 'in' ? 0.2 : -0.2)));
      canvas.style.transform = `scale(${zoom})`;
      closeCard();
    })
  );
}

document.addEventListener('DOMContentLoaded', () => {
  initNav();
  initSheet();
  initWaitlist();
  initInlineWaitlist();
  initToasts();
  initReveal();
  initMap();
});
